import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import { useFinancialYear } from "../context/FinancialYearContext";
import { useAuth } from "../context/AuthContext";

const styles = {
  page: { fontSize: 11, minHeight: "100%", overflow: "visible", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  box: {
    borderRadius: 12,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
    padding: 10,
    height: "100%",
  },
  statLabel: { fontSize: 10, fontWeight: 700 },
  statValue: { fontSize: 15, fontWeight: 800 },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 800,
    color: "#0d6efd",
    margin: "8px 0 5px",
  },
  tableWrap: {
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 450, marginBottom: 0 },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

const n = (v) => Number(v || 0);
const money = (v) => n(v).toFixed(2);
const get = (r, a, b) => r?.[a] ?? r?.[b];

export default function FinalAccountsPage() {
  const navigate = useNavigate();
  const { financialYear } = useFinancialYear();
  const { user } = useAuth();
  const [plRows, setPlRows] = useState([]);
  const [bsRows, setBsRows] = useState([]);
  const [fromDate, setFromDate] = useState(financialYear.fromDate || "");
  const [toDate, setToDate] = useState(financialYear.toDate || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFromDate(financialYear.fromDate || "");
    setToDate(financialYear.toDate || "");
  }, [financialYear.fromDate, financialYear.toDate]);

  const load = async () => {
    if (!fromDate || !toDate)
      return alert("Please select From Date and To Date");
    try {
      setLoading(true);
      const params = new URLSearchParams({ fromDate, toDate });
      if (user?.companyId) params.set("companyId", user.companyId);
      const [plRes, bsRes] = await Promise.all([
        client.get(`/finance/profit-loss?${params.toString()}`),
        client.get(`/finance/balance-sheet?${params.toString()}`),
      ]);
      setPlRows(Array.isArray(plRes.data) ? plRes.data : []);
      setBsRows(Array.isArray(bsRes.data) ? bsRes.data : []);
    } catch (err) {
      console.error(err);
      alert("Failed to load final accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const income = plRows.filter(
    (r) => get(r, "AccountType", "accountType") === "Income",
  );
  const expense = plRows.filter(
    (r) => get(r, "AccountType", "accountType") === "Expense",
  );
  const totalIncome = income.reduce(
    (s, r) => s + n(get(r, "Amount", "amount")),
    0,
  );
  const totalExpense = expense.reduce(
    (s, r) => s + n(get(r, "Amount", "amount")),
    0,
  );
  const netProfit = totalIncome - totalExpense;

  const assets = bsRows.filter(
    (r) => get(r, "AccountType", "accountType") === "Asset",
  );
  const liabilities = bsRows.filter(
    (r) => get(r, "AccountType", "accountType") === "Liability",
  );
  const equity = bsRows.filter(
    (r) => get(r, "AccountType", "accountType") === "Equity",
  );
  const totalAssets = assets.reduce(
    (s, r) => s + n(get(r, "Amount", "amount")),
    0,
  );
  const totalLiabilities = liabilities.reduce(
    (s, r) => s + n(get(r, "Amount", "amount")),
    0,
  );
  const totalEquity = equity.reduce(
    (s, r) => s + n(get(r, "Amount", "amount")),
    0,
  );
  const totalLiabilityEquity = totalLiabilities + totalEquity;
  const difference = totalAssets - totalLiabilityEquity;
  const isBalanced = Math.abs(difference) < 0.01;

  const warnings = useMemo(() => {
    const list = [];
    const negativeStock = assets.filter((r) => {
      const name = String(
        get(r, "AccountName", "accountName") || "",
      ).toLowerCase();
      return name.includes("inventory") && n(get(r, "Amount", "amount")) < 0;
    });
    if (negativeStock.length)
      list.push(
        "Inventory is negative. Check purchase stock additions, opening stock, stock adjustment and COGS postings.",
      );
    if (
      plRows.some((r) =>
        String(get(r, "AccountName", "accountName") || "")
          .toLowerCase()
          .includes("purchase"),
      )
    ) {
      list.push(
        "Purchase A/c is appearing in P&L. If you are using perpetual inventory, post purchases to Inventory and show only COGS in P&L.",
      );
    }
    if (!isBalanced)
      list.push(
        "Balance Sheet is not balanced. Review journal postings and opening balance entries.",
      );
    return list;
  }, [assets, plRows, isBalanced]);

  const openLedger = (row) => {
    const id = get(row, "AccountId", "accountId");
    if (!id || id === 0) return;
    const q = new URLSearchParams({ accountId: String(id), fromDate, toDate });
    navigate(`/ledger?${q.toString()}`);
  };

  const exportCsv = () => {
    const lines = [["Section", "Account Code", "Account Name", "Amount"]];
    const pushRows = (section, rows) =>
      rows.forEach((r) =>
        lines.push([
          section,
          get(r, "AccountCode", "accountCode") || "",
          get(r, "AccountName", "accountName") || "",
          money(get(r, "Amount", "amount")),
        ]),
      );
    pushRows("Expense", expense);
    pushRows("Income", income);
    pushRows("Asset", assets);
    pushRows("Liability", liabilities);
    pushRows("Equity", equity);
    lines.push(["Summary", "", "Net Profit/Loss", money(netProfit)]);
    lines.push(["Summary", "", "Difference", money(difference)]);
    const csv = lines
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `FinalAccounts_${fromDate}_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div
          style={styles.header}
          className="d-flex justify-content-between align-items-center"
        >
          <div>
            <h6 style={styles.title}>
              <i className="bi bi-file-earmark-spreadsheet me-2"></i>Final
              Accounts
            </h6>
            <div style={styles.sub}>
              Profit & Loss and Balance Sheet with current year profit/loss
              transfer
            </div>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-light btn-sm"
              style={styles.btn}
              onClick={exportCsv}
            >
              <i className="bi bi-file-earmark-excel me-1"></i>Excel/CSV
            </button>
            <button
              className="btn btn-light btn-sm"
              style={styles.btn}
              onClick={() => window.print()}
            >
              <i className="bi bi-printer me-1"></i>Print/PDF
            </button>
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2 align-items-end">
            <Field label="From Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Field>
            <Field label="To Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Field>
            <div className="col-md-2 d-grid">
              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={load}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load"}
              </button>
            </div>
          </div>

          <div className="row g-2 mb-2">
            <StatBox
              title="Net Result"
              value={`${netProfit >= 0 ? "Profit" : "Loss"}: ₹ ${money(Math.abs(netProfit))}`}
              color={netProfit >= 0 ? "#0f766e" : "#dc3545"}
            />
            <StatBox
              title="Total Assets"
              value={`₹ ${money(totalAssets)}`}
              color="#0f766e"
            />
            <div className="col-md-4">
              <div style={styles.box}>
                <div style={styles.statLabel}>Balance Status</div>
                <span
                  className={`badge ${isBalanced ? "text-bg-success" : "text-bg-danger"}`}
                  style={{ fontSize: 10 }}
                >
                  {isBalanced ? "Balanced" : "Not Balanced"}
                </span>
                <div
                  className={isBalanced ? "text-success" : "text-danger"}
                  style={{ fontSize: 11, fontWeight: 700, marginTop: 4 }}
                >
                  Difference: ₹ {money(Math.abs(difference))}
                </div>
              </div>
            </div>
          </div>

          {warnings.length > 0 && (
            <div
              className="alert alert-warning py-2 mb-2"
              style={{ fontSize: 11 }}
            >
              {warnings.map((w, i) => (
                <div key={i}>
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  {w}
                </div>
              ))}
            </div>
          )}

          <div style={styles.sectionTitle}>Profit & Loss Account</div>
          <div className="row g-2 mb-2">
            <ReportTable
              title="Expenses"
              rows={expense}
              totalLabel="Total Expense"
              total={totalExpense}
              color="#dc3545"
              maxHeight={220}
              onRowClick={openLedger}
            />
            <ReportTable
              title="Income"
              rows={income}
              totalLabel="Total Income"
              total={totalIncome}
              color="#0f766e"
              maxHeight={220}
              onRowClick={openLedger}
            />
          </div>

          <div style={styles.sectionTitle}>Balance Sheet</div>
          <div className="row g-2">
            <ReportTable
              title="Assets"
              rows={assets}
              totalLabel="Total Assets"
              total={totalAssets}
              color="#0f766e"
              maxHeight={250}
              onRowClick={openLedger}
            />
            <div className="col-md-6">
              <ReportTableInner
                title="Liabilities"
                rows={liabilities}
                totalLabel="Total Liabilities"
                total={totalLiabilities}
                color="#dc3545"
                maxHeight={150}
                onRowClick={openLedger}
              />
              <ReportTableInner
                title="Equity"
                rows={equity}
                totalLabel="Total Equity"
                total={totalEquity}
                color="#0d6efd"
                maxHeight={150}
                onRowClick={openLedger}
              />
              <div className="text-end fw-bold" style={{ fontSize: 11 }}>
                Liabilities + Equity: ₹ {money(totalLiabilityEquity)}
              </div>
            </div>
          </div>
          <div className="text-muted mt-2" style={{ fontSize: 10 }}>
            Tip: click any real account row to open its ledger. Current Year
            Profit/Loss is a system row calculated from P&L.
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}
function StatBox({ title, value, color }) {
  return (
    <div className="col-md-4">
      <div style={styles.box}>
        <div style={styles.statLabel}>{title}</div>
        <div style={{ ...styles.statValue, color }}>{value}</div>
      </div>
    </div>
  );
}
function ReportTable({
  title,
  rows,
  totalLabel,
  total,
  color,
  maxHeight,
  onRowClick,
}) {
  return (
    <div className="col-md-6">
      <ReportTableInner
        title={title}
        rows={rows}
        totalLabel={totalLabel}
        total={total}
        color={color}
        maxHeight={maxHeight}
        onRowClick={onRowClick}
      />
    </div>
  );
}
function ReportTableInner({
  title,
  rows,
  totalLabel,
  total,
  color,
  maxHeight,
  onRowClick,
}) {
  return (
    <>
      <h6 style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 5 }}>
        {title}
      </h6>
      <div style={{ ...styles.tableWrap, maxHeight, marginBottom: 8 }}>
        <table
          className="table table-bordered table-hover align-middle"
          style={styles.table}
        >
          <tbody>
            {!rows.length ? (
              <tr>
                <td
                  colSpan="2"
                  className="text-center text-muted"
                  style={{ ...styles.td, padding: 18 }}
                >
                  No records
                </td>
              </tr>
            ) : (
              rows.map((r, i) => {
                const id = get(r, "AccountId", "accountId");
                const isSystem = get(r, "IsSystemRow", "isSystemRow");
                return (
                  <tr
                    key={i}
                    onClick={() => onRowClick && onRowClick(r)}
                    style={{
                      cursor: id && !isSystem ? "pointer" : "default",
                      background: isSystem ? "#eef6ff" : undefined,
                    }}
                    title={id && !isSystem ? "Open Ledger" : ""}
                  >
                    <td style={styles.td}>
                      {get(r, "AccountName", "accountName")}{" "}
                      {id && !isSystem ? (
                        <i className="bi bi-box-arrow-up-right ms-1 text-muted"></i>
                      ) : null}
                    </td>
                    <td
                      className="text-end"
                      style={{
                        ...styles.td,
                        color:
                          n(get(r, "Amount", "amount")) < 0
                            ? "#dc3545"
                            : undefined,
                      }}
                    >
                      {Math.abs(n(get(r, "Amount", "amount"))).toFixed(2)}
                    </td>
                  </tr>
                );
              })
            )}
            <tr>
              <td
                style={{ ...styles.td, fontWeight: 700, background: "#f8f9fa" }}
              >
                {totalLabel}
              </td>
              <td
                className="text-end"
                style={{ ...styles.td, fontWeight: 700, color }}
              >
                {money(total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
